<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useQuery } from '@tanstack/vue-query';
import { FileService } from '@/services/file.services';

const route = useRoute();
const router = useRouter();
const { t } = useI18n({ useScope: 'global' });

const fileId = computed(() => route.params.id as string);

const { data, isPending, isError } = useQuery({
  queryKey: computed(() => ['media-detail', fileId.value]),
  queryFn: async () => {
    const res = await FileService.getFileById(fileId.value);
    const d = res.data as any;
    return d?.data ?? d;
  },
  enabled: computed(() => !!fileId.value),
});

const file = computed(() => data.value ?? null);

function fileThumb(f: any) {
  return f?.info?.secureUrl ?? f?.info?.url ?? null;
}
function fileName(f: any) {
  return f?.designation || f?.info?.publicId || t('medias.untitled');
}
function fileStoreId(f: any) {
  return f?.restaurant?.id ?? f?.restaurantId ?? null;
}
function restaurantName(f: any) {
  return f?.restaurant?.designation ?? fileStoreId(f) ?? '—';
}
function restaurantLogo(f: any) {
  return f?.restaurant?.logoUrl ?? null;
}

// ── Actions rapides ──────────────────────────────────────────────────────────
const copied = ref(false);
function viewFile() {
  const url = fileThumb(file.value);
  if (url) window.open(url, '_blank', 'noopener,noreferrer');
}
async function copyFileUrl() {
  const url = fileThumb(file.value);
  if (!url) return;
  try {
    await navigator.clipboard.writeText(url);
    copied.value = true;
    setTimeout(() => {
      copied.value = false;
    }, 1500);
  } catch {
    // clipboard indisponible : ignoré silencieusement
  }
}

// ── Suppression ──────────────────────────────────────────────────────────────
const isDeleting = ref(false);
const deleteError = ref('');
const confirmDeleteOpen = ref(false);
async function confirmDelete() {
  if (!file.value?.id) return;
  isDeleting.value = true;
  deleteError.value = '';
  try {
    await FileService.deleteFileById(file.value.id);
    router.push('/medias');
  } catch (err: any) {
    const msgs = err?.response?.data?.message;
    deleteError.value = Array.isArray(msgs)
      ? msgs.join(' · ')
      : msgs || t('medias.deleteError');
  } finally {
    isDeleting.value = false;
  }
}

function goBack() {
  router.push('/medias');
}
function goToStore() {
  const storeId = fileStoreId(file.value);
  if (!storeId) return;
  router.push(`/commerces/${storeId}?tab=medias`);
}
</script>

<template>
  <div class="md">
    <!-- Breadcrumb -->
    <nav class="md__bc">
      <button class="md__bc-link" @click="goBack">
        {{ t('medias.title') }}
      </button>
      <i class="pi pi-angle-right md__bc-sep" />
      <span class="md__bc-cur">{{
        file ? fileName(file) : fileId?.slice(0, 8)
      }}</span>
    </nav>

    <div v-if="isPending" class="md__loading">
      <i class="pi pi-spin pi-spinner" />
    </div>
    <div v-else-if="isError || !file" class="md__error">
      <i class="pi pi-exclamation-circle" />
      <p>{{ t('medias.detailError') }}</p>
      <button class="md__btn md__btn--ghost" @click="goBack">
        <i class="pi pi-arrow-left" /> {{ t('medias.title') }}
      </button>
    </div>

    <template v-else>
      <!-- Header -->
      <div class="md__header">
        <button class="md__back" @click="goBack">
          <i class="pi pi-arrow-left" />
        </button>
        <div class="md__header-info">
          <h1 class="md__name">{{ fileName(file) }}</h1>
          <p class="md__sub">{{ restaurantName(file) }}</p>
        </div>
      </div>

      <!-- Grille principale -->
      <div class="md__grid">
        <!-- Colonne gauche : aperçu -->
        <div class="md__col">
          <div class="md__card">
            <div class="md__thumb">
              <img
                v-if="fileThumb(file)"
                :src="fileThumb(file)"
                :alt="fileName(file)"
                loading="lazy"
              />
              <i v-else class="pi pi-image" />
            </div>
            <div class="md__thumb-actions">
              <button class="md__btn md__btn--ghost" @click="viewFile">
                <i class="pi pi-eye" /> {{ t('medias.view') }}
              </button>
              <button class="md__btn md__btn--ghost" @click="copyFileUrl">
                <i :class="copied ? 'pi pi-check' : 'pi pi-copy'" />
                {{ copied ? t('medias.copied') : t('medias.copyLink') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Colonne droite : infos -->
        <div class="md__col">
          <!-- Commerce associé -->
          <div class="md__card">
            <div class="md__card-hd">
              <h3 class="md__card-title">
                <i class="pi pi-shop" /> {{ t('medias.associatedStore') }}
              </h3>
            </div>
            <div class="md__store-row">
              <div class="md__store-logo">
                <img
                  v-if="restaurantLogo(file)"
                  :src="restaurantLogo(file)!"
                  :alt="restaurantName(file)"
                  loading="lazy"
                />
                <i v-else class="pi pi-shop" />
              </div>
              <span class="md__store-name">{{ restaurantName(file) }}</span>
            </div>
            <div class="md__actions">
              <button
                class="md__btn md__btn--ghost"
                :disabled="!fileStoreId(file)"
                @click="goToStore"
              >
                <i class="pi pi-arrow-right" /> {{ t('medias.viewStore') }}
              </button>
            </div>
          </div>

          <!-- Détails techniques -->
          <div class="md__card">
            <div class="md__card-hd">
              <h3 class="md__card-title">
                <i class="pi pi-info-circle" /> {{ t('medias.detailInfo') }}
              </h3>
            </div>
            <div class="md__info-view">
              <div class="md__info-row">
                <span class="md__info-label">{{ t('medias.format') }}</span>
                <span class="md__info-value">{{
                  file?.moreInfo?.format ?? '—'
                }}</span>
              </div>
              <div class="md__info-row">
                <span class="md__info-label">{{ t('medias.dimensions') }}</span>
                <span class="md__info-value">
                  {{ file?.moreInfo?.width ?? '—' }} ×
                  {{ file?.moreInfo?.height ?? '—' }}
                </span>
              </div>
              <div v-if="file?.moreInfo?.bytes" class="md__info-row">
                <span class="md__info-label">{{ t('medias.size') }}</span>
                <span class="md__info-value"
                  >{{ (file.moreInfo.bytes / 1024).toFixed(1) }} KB</span
                >
              </div>
              <div v-if="file?.createdAt" class="md__info-row">
                <span class="md__info-label">{{
                  t('paymentsMgmt.colDate')
                }}</span>
                <span class="md__info-value">{{
                  new Date(file.createdAt).toLocaleDateString()
                }}</span>
              </div>
            </div>
          </div>

          <!-- Zone de danger -->
          <div class="md__card md__danger-card">
            <div class="md__card-hd">
              <h3 class="md__card-title md__card-title--danger">
                <i class="pi pi-exclamation-triangle" />
                {{ t('medias.dangerZone') }}
              </h3>
            </div>
            <div class="md__edit-body">
              <p v-if="deleteError" class="md__error">
                <i class="pi pi-exclamation-circle" /> {{ deleteError }}
              </p>
              <div v-if="!confirmDeleteOpen" class="md__actions">
                <button
                  class="md__btn md__btn--danger"
                  @click="confirmDeleteOpen = true"
                >
                  <i class="pi pi-trash" /> {{ t('medias.delete') }}
                </button>
              </div>
              <div v-else class="md__confirm-delete">
                <p class="md__danger-text">{{ t('medias.deleteConfirm') }}</p>
                <div class="md__actions">
                  <button
                    class="md__btn md__btn--ghost"
                    @click="confirmDeleteOpen = false"
                  >
                    {{ t('medias.cancel') }}
                  </button>
                  <button
                    class="md__btn md__btn--danger"
                    :disabled="isDeleting"
                    @click="confirmDelete"
                  >
                    <span v-if="isDeleting" class="md__spinner" />
                    {{
                      isDeleting
                        ? t('medias.deleting')
                        : t('medias.deleteConfirmBtn')
                    }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.md {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2.5rem 3rem;
  box-sizing: border-box;
}
@media (max-width: 1023px) {
  .md {
    padding: 1.5rem;
  }
}

.md__bc {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}
.md__bc-link {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 0.8125rem;
  font-family: inherit;
  color: inherit;
}
.md__bc-link:hover {
  color: #0b1320;
}
.md__bc-sep {
  font-size: 0.625rem;
}
.md__bc-cur {
  font-weight: 700;
  color: var(--nxt-on-surface);
}

.md__loading,
.md__error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 4rem 1rem;
  color: var(--nxt-on-surface-muted);
}
.md__error i {
  font-size: 2rem;
  color: var(--nxt-error);
}

.md__header {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 1.5rem;
  padding: 1.5rem 1.75rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.04);
}
.md__back {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  flex-shrink: 0;
  border: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-surface-low);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9375rem;
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
}
.md__header-info {
  min-width: 0;
}
.md__name {
  margin: 0 0 0.15rem;
  font-size: 1.25rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-all;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
.md__sub {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}

/* Grille principale */
.md__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  align-items: start;
}
@media (max-width: 900px) {
  .md__grid {
    grid-template-columns: 1fr;
  }
}
.md__col {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.md__card {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 1.5rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}
.md__card-hd {
  padding: 1.125rem 1.5rem;
  border-bottom: 1px solid var(--nxt-outline-variant);
}
.md__card-title {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.md__card-title--danger {
  color: var(--nxt-error);
}

.md__thumb {
  width: 100%;
  aspect-ratio: 4 / 3;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface-muted);
  font-size: 2.5rem;
  overflow: hidden;
}
.md__thumb img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.md__thumb-actions {
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  padding: 1.25rem;
}

.md__store-row {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 1.5rem;
}
.md__store-logo {
  width: 3rem;
  height: 3rem;
  min-width: 3rem;
  border-radius: 50%;
  overflow: hidden;
  background: var(--nxt-surface-low);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--nxt-on-surface-muted);
  font-size: 1.125rem;
}
.md__store-logo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.md__store-name {
  font-size: 1rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}

.md__info-view {
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  padding: 1.5rem;
}
.md__info-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  font-size: 0.875rem;
}
.md__info-label {
  color: var(--nxt-on-surface-muted);
  font-weight: 600;
}
.md__info-value {
  color: var(--nxt-on-surface);
  font-weight: 700;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 60%;
}

.md__edit-body {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
}
.md__error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  color: var(--nxt-error);
  margin: 0;
}

.md__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.125rem 1.5rem;
  border-top: 1px solid var(--nxt-outline-variant);
}
.md__actions .md__btn {
  flex: 1;
  justify-content: center;
}
.md__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  height: 2.5rem;
  padding: 0 1.25rem;
  border-radius: 0.625rem;
  font-size: 0.875rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  border: none;
}
.md__btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.md__btn--ghost {
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface-variant);
  border: 1px solid var(--nxt-outline-variant);
}
.md__btn--danger {
  background: var(--nxt-error);
  color: #fff;
  box-shadow: 0 2px 8px rgba(220, 38, 38, 0.35);
}
.md__spinner {
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  display: inline-block;
  border: 2px solid rgba(11, 19, 32, 0.2);
  border-top-color: #0b1320;
  animation: md-spin 0.7s linear infinite;
}
@keyframes md-spin {
  to {
    transform: rotate(360deg);
  }
}

.md__danger-card {
  border-color: rgba(220, 38, 38, 0.3);
}
.md__danger-text {
  margin: 0;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-muted);
  line-height: 1.5;
}
.md__confirm-delete {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
</style>

<style>
[data-theme='dark'] .md__header,
[data-theme='dark'] .md__card {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .md__back,
[data-theme='dark'] .md__btn--ghost {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.7);
  border-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .md__thumb,
[data-theme='dark'] .md__store-logo {
  background: rgba(255, 255, 255, 0.05);
}
</style>
